/**
 * The landing page of the documentation: what FunderGraph is in four
 * sentences, what it holds today from the build itself, where each reader
 * starts, and how to reach the project. The page list is a live block, so
 * this page can never name a page that does not exist.
 */
import { COPY } from '../../copy.js';
import type { DocPage } from '../types.js';

export const aboutPage: DocPage = {
  slug: 'about',
  title: 'About FunderGraph',
  audience: 'everyone',
  promise: 'What FunderGraph is, what it holds today, and where each kind of reader should start.',
  lead: 'FunderGraph is a map of who funds what among United States nonprofits, read from the returns they file. This page says what it is, what it holds today, and where to start.',
  sections: [
    {
      id: 'what',
      heading: 'What FunderGraph is',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'FunderGraph reads the returns nonprofits file with the Internal Revenue Service and the master file of exempt organizations, and holds each grant those returns report with the return that reported it. It resolves the names funders write on their grant lists to the organizations the IRS lists, so that a grant to a name becomes a grant to a page. It computes, from the grants alone, which organizations share funders, and shows that as a map with its method beside it. Every fact names its source and its period, and nothing on the site names a person.',
          ],
        },
      ],
    },
    {
      id: 'holds',
      heading: 'What it holds today',
      blocks: [
        { kind: 'headline' },
        {
          kind: 'paragraph',
          text: [
            'The graph is rebuilt from the returns the IRS had released when it was built. A return can lag its fiscal year by a year or more, so the most recent year is always incomplete; ',
            { link: '/about/reading#freshness', text: 'the reading page says how to read the dates' },
            '.',
          ],
        },
      ],
    },
    {
      id: 'start',
      heading: 'Where to start',
      blocks: [
        {
          kind: 'list',
          items: [
            ['If you know an organization, type its name or EIN in the box at the top of any page.'],
            ['If you want to know who funds a kind of work, start from a subject on the ', { link: '/', text: 'home page' }, ' and choose a state.'],
            ['If you want to know what a page is telling you, read ', { link: '/about/reading', text: 'how FunderGraph reads a filing' }, ' first.'],
          ],
        },
      ],
    },
    { id: 'pages', heading: 'The pages', blocks: [{ kind: 'pages' }] },
    {
      id: 'working',
      heading: 'Working with FunderGraph',
      blocks: [
        {
          kind: 'paragraph',
          text: [
            'FunderGraph is one person’s build. The site is the way in: it is open to read, and accounts are coming. There is no roadmap to promise and no program to join. Correspondence about research use, about a fact that is wrong, or about an organization that is represented badly is welcome, and is answered as time allows.',
          ],
        },
      ],
    },
    {
      id: 'contact',
      heading: 'Reaching the project',
      blocks: [
        {
          kind: 'paragraph',
          text: ['Write to ', { link: `mailto:${COPY.site.contact}`, text: COPY.site.contact }, '. For a correction, say which page and which fact, and what the filing or the record says instead.'],
        },
      ],
    },
  ],
};
